// Adattatore degli export custom: gli ID della fonte distinguono le squadre dello stesso club.
export async function prepareCustomFootballImport(files, seasonId) {
	const packs = new Map();
	for (const file of files) {
		const data = JSON.parse((await file.text()).replace(/^\uFEFF/, ""));
		const kinds = ["teams", "players", "matches"].filter((k) =>
			Array.isArray(data[k]),
		);
		if (kinds.length !== 1) throw Error("File non riconosciuto: " + file.name);
		const kind = kinds[0];
		let prefix = file.name.split("_" + kind + "_")[0];
		if (["Serie_A", "Serie_A_Enilive"].includes(prefix)) prefix = "Serie_A";
		const pack = packs.get(prefix) || {};
		if (pack[kind])
			throw Error(
				"Più file " +
					kind +
					" per " +
					prefix +
					": scegli un solo aggiornamento.",
			);
		pack[kind] = data;
		packs.set(prefix, pack);
	}
	const teams = new Map(),
		players = new Map(),
		competitions = [],
		matches = [],
		warnings = [];
	const localTime = new Intl.DateTimeFormat("sv-SE", {
		timeZone: "Europe/Rome",
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
		hourCycle: "h23",
	});
	for (const [prefix, pack] of packs) {
		if (!pack.teams || !pack.players || !pack.matches)
			throw Error("Mancano file squadre, giocatori o partite per " + prefix);
		const competition = pack.teams.competition;
		if (
			pack.players.competition?.id !== competition.id ||
			pack.players.season?.id !== pack.teams.season?.id
		)
			throw Error("Competizione o stagione discordante in " + prefix);
		const cid = String(competition.id);
		competitions.push({
			externalId: cid,
			name: competition.name,
			sourceSeasonId: String(pack.teams.season.id),
		});
		const ids = new Set(pack.teams.teams.map((t) => String(t.id)));
		for (const t of pack.teams.teams) {
			const key = String(t.id);
			let clubKey = t.name.replace(/\s+U\d+$/, "").trim();
			if (t.name === "Juventus Next Gen") clubKey = "Juventus";
			if (clubKey === "Bari 1908") clubKey = "Bari";
			const row = teams.get(key) || {
				externalId: key,
				name: t.name,
				clubKey,
				competitions: [],
			};
			if (row.name !== t.name)
				throw Error(
					"Nome discordante per una squadra dello stesso identificativo.",
				);
			row.competitions.push(cid);
			teams.set(key, row);
			const age = t.name.match(/U\d+/)?.[0],
				shortAge = t.shortName?.match(/U\d+/)?.[0];
			if (age && shortAge && age !== shortAge)
				warnings.push(
					t.name + ": nome breve " + t.shortName + ", categoria da verificare.",
				);
		}
		for (const person of pack.players.players) {
			const key = String(person.id),
				assigned = [
					...new Set(
						(person.teams || [])
							.map((t) => String(t.id))
							.filter((id) => ids.has(id)),
					),
				];
			if (!assigned.length)
				throw Error("Giocatore senza squadra iscritta: " + person.shortName);
			const previous = players.get(key);
			if (
				previous &&
				["firstName", "lastName", "birthDate"].some(
					(k) => previous[k] !== person[k],
				)
			)
				throw Error("Identità discordante per " + person.shortName);
			const row = previous || {
				externalId: key,
				firstName: person.firstName,
				lastName: person.lastName,
				birthDate: person.birthDate,
				roleType: person.roleType,
				nationality: (person.nationalities || []).map((n) => n.name).join(", "),
				foot: person.foot,
				height: person.height,
				weight: person.weight,
				teamIds: [],
			};
			row.teamIds = [...new Set([...row.teamIds, ...assigned])];
			players.set(key, row);
			if (assigned.length > 1)
				warnings.push(
					person.shortName + ": appartenenza a più rose conservata.",
				);
			if (!person.firstName)
				warnings.push(person.shortName + ": nome non fornito.");
			if (!person.roleType)
				warnings.push(person.shortName + ": ruolo non fornito.");
		}
		for (const m of pack.matches.matches) {
			const home = String(m.homeTeam.id),
				away = String(m.awayTeam.id);
			if (!ids.has(home) || !ids.has(away) || home === away)
				throw Error("Squadre non valide in una partita di " + prefix);
			const stamp = new Date(m.date);
			if (Number.isNaN(stamp.getTime()))
				throw Error("Data partita non valida.");
			const unknown =
				stamp.getUTCHours() === 0 &&
				stamp.getUTCMinutes() === 0 &&
				stamp.getUTCSeconds() === 0;
			const parts = Object.fromEntries(
				localTime
					.formatToParts(stamp)
					.filter((p) => p.type !== "literal")
					.map((p) => [p.type, p.value]),
			);
			const startDate = unknown
				? m.date.slice(0, 10) + "T00:00:00"
				: `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`;
			matches.push({
				externalId: String(m.id),
				competitionId: cid,
				homeTeamId: home,
				awayTeamId: away,
				startDate,
				timeToBeConfirmed: unknown,
				homeScore: m.homeScore,
				awayScore: m.awayScore,
				groupId: String(m.group.id),
				groupName: m.group.name,
				roundId: String(m.turn.id),
				roundName: m.turn.name,
				roundNumber: Number(m.turn.name),
			});
		}
	}
	if (
		!competitions.length ||
		new Set(matches.map((m) => m.externalId)).size !== matches.length
	)
		throw Error("Pacchetto vuoto o partite duplicate.");
	return {
		source: "custom",
		seasonId,
		competitions,
		teams: [...teams.values()],
		players: [...players.values()],
		matches,
		warnings: [...new Set(warnings)],
	};
}
